import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Loader2,
  Package,
  Layers,
  ChevronDown,
  Printer,
  Eye
} from 'lucide-react';

import { api } from '../services/api';
import CategoryIcon from '../components/CategoryIcon';
import CatalogBreadcrumbs from '../components/CatalogBreadcrumbs';
import RichContent, { plainText } from '../components/RichContent';
import SpecValue from '../components/SpecValue';
import { Button } from '../components/ui/button';
import QuantityControl from '../components/QuantityControl';
import { usePreinvoice } from '../context/PreinvoiceContext';
import { useToast } from '../context/ToastContext';
import { useWebAuth } from '../context/WebAuthContext';
import LoginModal from '../components/LoginModal';
import { trackProductEvent } from '../services/tracking';
import { isWeightKey } from '../utils/weight';

// گرفتن مقدار یک پارامتر از query string
function useQueryParam(name) {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get(name) || '';
  }, [search, name]);
}

function cleanCell(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/<[^>]*>/g, '').trim();
}

function getProductSpecs(product) {
  const specs = product?.tech_data?.specs || product?.specs || product?.tech_data || {};
  if (!specs || typeof specs !== 'object' || Array.isArray(specs)) return {};
  return specs;
}

function isHiddenSpecKey(key) {
  const normalized = String(key || '').trim().toLowerCase();
  return [
    'image_url',
    'image url',
    'product_image',
    'show_product_link',
    'show product link',
    'can_view_product',
    'show_product',
    'weight',
    'weight_kg',
    'unit_weight',
    'وزن',
  ].includes(normalized);
}

function getSpecKeys(rows) {
  const keys = [];
  const seen = new Set();
  rows.forEach((row) => {
    Object.entries(getProductSpecs(row)).forEach(([key, value]) => {
      if (isWeightKey(key) || isHiddenSpecKey(key) || value === null || value === undefined || String(value) === '' || seen.has(key)) return;
      seen.add(key);
      keys.push(key);
    });
  });
  return keys;
}

function getBrandText(product) {
  if (Array.isArray(product?.brands) && product.brands.length) {
    return product.brands.map((b) => b?.name || b?.title).filter(Boolean).join('، ');
  }
  return product?.brand_title || product?.brandTitle || product?.brand_name || product?.brandName || '';
}

function buildRowsTableHtml(rows, title = 'products') {
  const safeRows = Array.isArray(rows) ? rows : [];
  const specKeys = getSpecKeys(safeRows);
  const columns = [
    ['row', 'ردیف'],
    ['title', 'شرح کالا'],
    ['brand', 'برند'],
    ['unit', 'واحد'],
    ['price', 'قیمت'],
    ...specKeys.map((key) => [`spec:${key}`, key]),
  ];
  const cell = (value) => `<td>${cleanCell(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
  const rowsHtml = safeRows.map((row, index) => {
    const specs = getProductSpecs(row);
    return `<tr>${columns.map(([key]) => {
      if (key === 'row') return cell(index + 1);
      if (key === 'title') return cell(row.title || row.name || '');
      if (key === 'brand') return cell(getBrandText(row));
      if (key === 'unit') return cell(row.unit || '');
      if (key === 'price') return cell(row.price ? Number(row.price).toLocaleString('fa-IR') : 'استعلام');
      if (key.startsWith('spec:')) return cell(specs[key.slice(5)] || '');
      return cell('');
    }).join('')}</tr>`;
  }).join('');

  return `<!doctype html>
    <html lang="fa" dir="rtl">
      <head>
        <meta charset="utf-8" />
        <title>${cleanCell(title)}</title>
        <style>
          body { font-family: Tahoma, Arial, sans-serif; margin: 24px; color: #111827; }
          h1 { font-size: 18px; margin: 0 0 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #d1d5db; padding: 8px; text-align: right; }
          th { background: #eef6ff; font-weight: 700; }
          tr:nth-child(even) td { background: #fafafa; }
          @page { size: landscape; margin: 12mm; }
        </style>
        <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 300); });</script>
      </head>
      <body>
        <h1>${cleanCell(title)}</h1>
        <table>
          <thead><tr>${columns.map(([, label]) => `<th>${label}</th>`).join('')}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
    </html>`;
}

function printRowsAsPdf(rows, title = 'products') {
  const html = buildRowsTableHtml(rows, title);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank', 'width=1200,height=800');
  if (!printWindow) {
    URL.revokeObjectURL(url);
    return;
  }
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function ProductRowsTable({
  rows,
  title,
  categorySlug,
  source,
  selectedBrands,
  setSelectedBrands,
  isAuthed,
  setLoginOpen,
  addItem,
  showToast,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const specKeys = getSpecKeys(safeRows);
  const [rowQuantities, setRowQuantities] = useState({});

  const canShowProduct = (product) =>
    Boolean(
      product?.can_view_product ||
      product?.show_product_link ||
      product?.show_product ||
      product?.is_publicly_viewable ||
      product?.tech_data?.show_product_link
    );

  const getRowQuantity = (product) => rowQuantities[product.id] ?? 1;
  const setRowQuantity = (productId, next) => setRowQuantities((prev) => ({ ...prev, [productId]: next }));

  const addProduct = (product) => {
    const quantity = Math.max(Number(getRowQuantity(product)) || 1, 1);
    const productBrands = Array.isArray(product.brands) ? product.brands.filter(Boolean) : [];
    const selectedBrandId =
      selectedBrands[product.id] ||
      (productBrands.length === 1 ? productBrands[0]?.id : '') ||
      '';
    const selectedBrand = productBrands.find((b) => String(b.id) === String(selectedBrandId));

    if (!isAuthed) {
      setLoginOpen(true);
      return;
    }
    if (productBrands.length > 1 && !selectedBrand) {
      showToast({
        title: 'برند را انتخاب کنید',
        message: 'برای این محصول چند برند ثبت شده است؛ لطفا برند موردنظر را انتخاب کنید.',
        type: 'warning',
      });
      return;
    }

    addItem({
      ...product,
      selected_brand_id: selectedBrand?.id || null,
      selected_brand_name: selectedBrand
        ? (selectedBrand.name || selectedBrand.title)
        : (productBrands[0]?.name || productBrands[0]?.title || getBrandText(product) || null),
      product_code: product.product_code || product.sku || product.external_code || product.barcode,
    }, quantity);
    trackProductEvent('add_to_preinvoice', {
      product_item_id: product.id || product.product_item_id || null,
      brand_id: selectedBrand?.id || null,
      category_id: product.category_id || null,
      category_slug: product.category_slug || categorySlug,
      source,
      metadata: { title: product.title || '', sku: product.sku || product.product_code || '', quantity },
    });
    showToast({
      title: 'به پیش‌فاکتور اضافه شد',
      message: `${product.title || product.name || 'محصول'} به سبد پیش‌فاکتور شما اضافه شد.`,
      type: 'success',
    });
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-100 bg-blue-50/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="break-words text-sm font-extrabold text-slate-900">{title}</h2>
          <p className="mt-1 text-xs text-gray-500">{safeRows.length.toLocaleString('fa-IR')} ردیف محصول</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => printRowsAsPdf(safeRows, title)}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-extrabold text-blue-700 transition hover:bg-blue-50"
          >
            <Printer className="h-4 w-4" />
            چاپ
          </button>
        </div>
      </div>

      <div className="overflow-auto">
        <table className="w-full min-w-[920px] text-center text-xs sm:text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 text-center">ردیف</th>
              <th className="px-4 py-3 text-center">شرح کالا</th>
              <th className="px-4 py-3 text-center">برند</th>
              <th className="px-4 py-3 text-center">واحد</th>
              <th className="px-4 py-3 text-center">قیمت</th>
              {specKeys.map((key) => (
                <th key={key} className="px-4 py-3 text-center">{key}</th>
              ))}
              <th className="px-4 py-3 text-center">عملیات</th>
            </tr>
          </thead>
          <tbody>
            {safeRows.map((product, index) => {
              const productBrands = Array.isArray(product.brands) ? product.brands.filter(Boolean) : [];
              const selectedBrandId =
                selectedBrands[product.id] ||
                (productBrands.length === 1 ? productBrands[0]?.id : '') ||
                '';
              const specs = getProductSpecs(product);

              return (
                <tr key={product.id || product.sku || product.title} className="border-t border-gray-100 transition hover:bg-slate-100/70">
                  <td className="px-4 py-3 text-gray-500">{(index + 1).toLocaleString('fa-IR')}</td>
                  <td className="px-4 py-3 font-bold text-slate-900">{product.title || product.name || '-'}</td>
                  <td className="px-4 py-3">
                    {productBrands.length > 1 ? (
                      <select
                        value={selectedBrandId}
                        onChange={(e) => setSelectedBrands((prev) => ({ ...prev, [product.id]: e.target.value }))}
                        className="min-w-32 rounded-xl border border-gray-200 bg-white px-3 py-2 text-center text-xs"
                      >
                        <option value="">انتخاب برند</option>
                        {productBrands.map((brand) => (
                          <option key={brand.id} value={brand.id}>{brand.name || brand.title || 'برند'}</option>
                        ))}
                      </select>
                    ) : (
                      getBrandText(product) || '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{product.unit || '-'}</td>
                  <td className="px-4 py-3 font-bold">
                    {product.price ? (
                      <span className="text-emerald-700">{Number(product.price).toLocaleString('fa-IR')}</span>
                    ) : (
                      <span title="برای استعلام تماس بگیرید" className="cursor-help text-[#005bbb]">استعلام</span>
                    )}
                  </td>
                  {specKeys.map((key) => (
                    <td key={key} className="px-4 py-3 text-gray-700"><SpecValue name={key} value={specs[key] ?? '-'} /></td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <QuantityControl
                        value={getRowQuantity(product)}
                        onChange={(next) => setRowQuantity(product.id, next)}
                        className="h-8 rounded-full"
                        inputClassName="h-8 w-11 text-xs"
                        buttonClassName="h-6 w-6"
                      />
                      {canShowProduct(product) ? (
                        <Link to={`/product/${product.id}`} className="inline-flex h-8 items-center gap-1 rounded-full border border-gray-200 px-3 text-xs font-bold text-blue-700 hover:bg-blue-50">
                          <Eye className="h-3.5 w-3.5" />
                          نمایش محصول
                        </Link>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => addProduct(product)}
                        className="h-8 rounded-full bg-[#005bbb] px-3 text-xs font-bold text-white hover:bg-[#004a99]"
                      >
                        افزودن
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!safeRows.length && (
              <tr>
                <td className="px-4 py-8 text-center text-gray-500" colSpan={6 + specKeys.length}>
                  ردیفی برای این جدول ثبت نشده است.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SubcategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { addItem } = usePreinvoice();
  const { showToast } = useToast();
  const { isAuthed } = useWebAuth();

  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [children, setChildren] = useState([]);
  const [products, setProducts] = useState([]); // محصولات

  const [brands, setBrands] = useState([]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [l4Loading, setL4Loading] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedChildren, setExpandedChildren] = useState({});
  const [childProducts, setChildProducts] = useState({});
  const [childProductsLoading, setChildProductsLoading] = useState(false);

  // --- برای Infinite Scroll ---
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 12;

  // هماهنگ‌سازی فیلتر برند با URL
  const brandFromQuery = useQueryParam('brand');
  const [brandFilter, setBrandFilter] = useState(brandFromQuery || '');

  useEffect(() => {
    setBrandFilter(brandFromQuery || '');
    // هر بار فیلتر برند یا slug عوض شد، از اول لود کن
    setOffset(0);
    setHasMore(true);
    setProducts([]);
  }, [brandFromQuery, slug]);

  // بارگذاری دسته و زیرمجموعه‌ها (وابسته به slug، مستقل از offset)
  useEffect(() => {
    let mounted = true;

    const loadCategory = async () => {
      setLoading(true);
      try {
        const catRes = await api.getCategoryBySlug(slug);
        if (!mounted) return;
        setCategory(catRes?.category || null);
        setChildren(Array.isArray(catRes?.children) ? catRes.children : []);
      } catch (e) {
        console.warn('Error loading category:', e);
        if (!mounted) return;
        setCategory(null);
        setChildren([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    loadCategory();

    return () => {
      mounted = false;
    };
  }, [slug]);

  const showChildrenAsAccordions =
    children.length > 0 && children.every((child) => child?.has_children === false);
  const visibleAccordionChildren = useMemo(() => children.filter((child) => !brandFilter || (childProducts[child.slug] || []).length > 0), [children, brandFilter, childProducts]);

  useEffect(() => {
    setExpandedChildren({});
  }, [slug, brandFilter]);

  useEffect(() => {
    if (showChildrenAsAccordions && !childProductsLoading && visibleAccordionChildren.length === 1) {
      const childSlug = visibleAccordionChildren[0].slug;
      setExpandedChildren((prev) => Object.hasOwn(prev, childSlug) ? prev : { ...prev, [childSlug]: true });
    }
  }, [showChildrenAsAccordions, childProductsLoading, visibleAccordionChildren]);

  useEffect(() => {
    if (!showChildrenAsAccordions) {
      setChildProducts({});
      return;
    }

    let mounted = true;
    setChildProductsLoading(true);
    Promise.all(
      children.map(async (child) => {
        try {
          const fetchParams = {
            category: child.slug,
            limit: 100,
            offset: 0
          };
          if (brandFilter) fetchParams.brand = brandFilter;
          const pr = await api.getProducts(fetchParams);
          const data = Array.isArray(pr?.data) ? pr.data : Array.isArray(pr) ? pr : [];
          return [child.slug, data];
        } catch {
          return [child.slug, []];
        }
      })
    )
      .then((entries) => {
        if (!mounted) return;
        setChildProducts(Object.fromEntries(entries));
      })
      .finally(() => mounted && setChildProductsLoading(false));

    return () => {
      mounted = false;
    };
  }, [showChildrenAsAccordions, children, brandFilter]);


  // بارگذاری محصولات با پشتیبانی از brandFilter + infinite scroll
  useEffect(() => {
    let mounted = true;

    const loadProducts = async () => {
      // اگر hasMore=false یعنی آخر لیست است، دیگر درخواست نده
      if (!hasMore && offset !== 0) return;

      setL4Loading(true);

      try {
        const fetchParams = {
          category: slug,
          limit: LIMIT,
          offset
        };

        if (brandFilter) {
          fetchParams.brand = brandFilter;
        }

        const pr = await api.getProducts(fetchParams);
        if (!mounted) return;

        const data = Array.isArray(pr?.data)
          ? pr.data
          : Array.isArray(pr)
          ? pr
          : [];

        if (offset === 0) {
          // بار اول
          setProducts(data);
        } else {
          // اضافه کردن به لیست قبلی
          setProducts(prev => [...prev, ...data]);
        }

        // اگر تعداد آیتم‌های دریافتی کمتر از LIMIT بود یعنی دیگر محصولی نیست
        setHasMore(data.length === LIMIT);
      } catch (e) {
        console.error('Error loading products:', e);
        if (!mounted) return;
        if (offset === 0) {
          setProducts([]);
        }
        setHasMore(false);
      } finally {
        if (!mounted) return;
        setL4Loading(false);
      }
    };

    loadProducts();

    return () => {
      mounted = false;
    };
  }, [slug, brandFilter, offset]);

  // listener برای Infinite Scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || l4Loading) return;
      // نزدیک انتهای صفحه
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 400
      ) {
        setOffset(prev => prev + LIMIT);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, l4Loading]);

  // گرفتن لیست برندها برای فیلتر (یک بار در شروع)
  useEffect(() => {
    let mounted = true;
    api
      .getCategoryBrands(slug)
      .then(res => {
        if (!mounted) return;
        const list = Array.isArray(res?.brands)
          ? res.brands
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        setBrands(list);
      })
      .catch(() => mounted && setBrands([]));
    return () => {
      mounted = false;
    };
  }, [slug]);

  const visibleBrands = brands;

  // آیا این دسته زیرمجموعه دارد؟
  const displaySubcategories = children && children.length > 0 && !showChildrenAsAccordions;

  const renderSpecs = p => {
    const specs = p.tech_data?.specs || p.specs || null;
    if (!specs || typeof specs !== 'object' || Object.keys(specs).length === 0)
      return null;

    return (
      <div className="mt-4 grid grid-cols-2 gap-2 border-t pt-4">
        {Object.entries(specs).map(([k, v]) => (
          <div
            key={k}
            className="flex justify-between text-xs bg-gray-50 p-2 rounded"
          >
            <span className="text-gray-500">{k}:</span>
            <span className="font-medium text-gray-800">{String(v)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !category) {
    // Skeleton ساده‌ی لود اولیه
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-500">در حال دریافت اطلاعات...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* HERO SECTION */}
      <div className="bg-white border-b">
        <div className="pp-container py-10">
          <div className="mb-6"><CatalogBreadcrumbs items={category?.breadcrumbs || (category ? [category] : [])} /></div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <div className="inline-flex items-center gap-2 text-xs text-blue-700 bg-blue-50 px-3 py-1 rounded-full mb-4">
                <Layers size={14} />
                {displaySubcategories ? 'گروه محصولات' : 'محصولات'}
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-3">
                {category?.title || slug}
              </h1>
              {category?.description && (
                <RichContent html={category.description} className="text-slate-600 max-w-xl text-sm leading-relaxed" />
              )}
            </div>
            {(category?.hero_image_url || category?.image_url) && (
              <div className="w-48 h-48 rounded-xl overflow-hidden bg-gray-100 shadow-lg p-3 border border-gray-200">
                <img
                  src={category.hero_image_url || category.image_url}
                  alt={category?.title}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="pp-container mt-10 space-y-6">
        {/* فیلتر برندها - chip style + اسکرول افقی */}
        {visibleBrands.length > 0 && !displaySubcategories && (
          <div className="rounded-3xl border border-blue-100 bg-blue-50/45 px-4 py-3 shadow-[0_8px_24px_rgba(0,91,187,0.06)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="shrink-0 text-sm font-bold text-slate-700">فیلتر بر اساس برند</div>
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-start gap-2">
              <Button
                variant={!brandFilter ? 'default' : 'outline'}
                className="h-9 whitespace-nowrap rounded-full px-4 text-xs font-extrabold"
                onClick={() => {
                  navigate(`/subcategory/${slug}`, { replace: true });
                }}
              >
                همه برندها
              </Button>
              {visibleBrands.map(b => (
                <Button
                  key={b.slug}
                  variant={brandFilter === b.slug ? 'default' : 'outline'}
                  className="h-9 whitespace-nowrap rounded-full px-4 text-xs font-extrabold"
                  onClick={() => {
                    navigate(`/subcategory/${slug}?brand=${b.slug}`, {
                      replace: true
                    });
                  }}
                >
                  {b.title || b.name}
                </Button>
              ))}
              </div>
            </div>
          </div>
        )}

        {showChildrenAsAccordions ? (
          <div className="space-y-4">
            {childProductsLoading && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>در حال دریافت جدول‌های سطح ۵...</span>
              </div>
            )}
            {visibleAccordionChildren
              .map((child) => {
              const isChildOpen = !!expandedChildren[child.slug];
              const rows = childProducts[child.slug] || [];
              return (
                <section key={child.slug} className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                  <button
                    type="button"
                    onClick={() => setExpandedChildren((prev) => ({ ...prev, [child.slug]: !prev[child.slug] }))}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-right hover:bg-gray-50"
                  >
                    <div>
                      <div className="text-sm font-extrabold text-slate-900">{child.title}</div>
                      {(child.short_desc || child.description) ? <div className="mt-1 text-xs leading-6 text-gray-500">{plainText(child.short_desc || child.description)}</div> : null}
                    </div>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-blue-700 transition-transform ${isChildOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isChildOpen && (
                    <div className="border-t border-gray-100 p-4">
                      <ProductRowsTable
                        rows={rows}
                        title={child.title}
                        categorySlug={child.slug}
                        source="subcategory_level5_accordion"
                        selectedBrands={selectedBrands}
                        setSelectedBrands={setSelectedBrands}
                        isAuthed={isAuthed}
                        setLoginOpen={setLoginOpen}
                        addItem={addItem}
                        showToast={showToast}
                      />
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        ) : displaySubcategories ? (
          // --- نمایش گروه‌ها ---
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map(child => {
              const childLink = brandFilter
                ? `/subcategory/${child.slug}?brand=${brandFilter}`
                : `/subcategory/${child.slug}`;
              return (
                <Link
                  key={child.slug}
                  to={childLink}
                  className="bg-white p-6 rounded-xl border border-transparent hover:border-blue-300 shadow-sm hover:shadow-md transition-all flex items-center justify-between group"
                >
                  <div>
                    <div className="inline-flex items-center gap-2 text-[11px] text-blue-700 bg-blue-50 px-3 py-1 rounded-full mb-3">
                      <Layers size={12} />
                      <span>گروه محصول</span>
                    </div>
                    <h2 className="font-semibold text-slate-900 mb-1">
                      {child.title}
                    </h2>
                    {(child.short_desc || child.description) && (
                      <p className="text-xs text-gray-500">
                        {plainText(child.short_desc || child.description)}
                      </p>
                    )}
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors">
                    <CategoryIcon
                      category={child}
                      fallback={<Package size={22} />}
                      imageClassName="h-8 w-8 object-contain"
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          // --- نمایش مستقیم محصولات ---
          <div className="space-y-3">
            {l4Loading && products.length === 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>در حال دریافت لیست محصولات...</span>
              </div>
            )}

            {products.length === 0 && !l4Loading && (
              <div className="bg-white rounded-xl border p-10 flex flex-col items-center justify-center text-center">
                <Package className="w-10 h-10 text-gray-300 mb-3" />
                <h3 className="font-semibold text-slate-800 mb-1">
                  محصولی یافت نشد
                </h3>
                <p className="text-xs text-gray-500 max-w-sm">
                  در حال حاضر محصولی برای این دسته ثبت نشده است. لطفاً بعداً
                  مجدداً بررسی کنید یا با تیم فروش تماس بگیرید.
                </p>
              </div>
            )}

            <ProductRowsTable
              rows={products}
              title={category?.title || slug}
              categorySlug={slug}
              source="subcategory_page"
              selectedBrands={selectedBrands}
              setSelectedBrands={setSelectedBrands}
              isAuthed={isAuthed}
              setLoginOpen={setLoginOpen}
              addItem={addItem}
              showToast={showToast}
            />

            {/* لودر انتهای لیست برای Infinite Scroll */}
            {l4Loading && products.length > 0 && (
              <div className="flex items-center justify-center mt-4 text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin ml-2" />
                در حال بارگذاری محصولات بیشتر...
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <div className="flex items-center justify-center mt-4 text-[11px] text-gray-400">
                همه‌ی محصولات این دسته نمایش داده شد.
              </div>
            )}
          </div>
        )}
        <RichContent html={category?.bottom_description} className="mt-10 text-sm text-gray-700" />
      </div>
    </div>
  );
}
